import CodeTabs from "./src/components/CodeTabs";
import pageMeta from "./src/page-meta.json";
// Side-effect imports: initialize PostHog + mount the cookie-consent banner, and
// sync the cross-subdomain theme cookie (browser only — no-ops while Zudoku prerenders).
import "./src/lib/mount-cookie-banner";
import "./src/lib/theme-sync";
import { FooterContent } from "@regexsolver/web-shared/components/FooterContent";
import { FONT_PRELOAD } from "@regexsolver/web-shared/fonts/preload";
import {
  NAV_ITEMS,
  isExternalSiteLink,
  isSiteNavGroup,
  resolveSiteLink,
} from "@regexsolver/web-shared/site-links";
import { generateShortSnippet } from "./src/generateCode";
import { ZudokuConfig } from "zudoku";

// Same origin mapping the website builds from NEXT_PUBLIC_* (Header/Footer.tsx).
// Unset vars fall back to relative paths (404 in isolated local dev).
const ORIGINS = {
  website: process.env.ZUDOKU_PUBLIC_WEBSITE_URL ?? "",
  docs: "",
  console: process.env.ZUDOKU_PUBLIC_CONSOLE_URL ?? "",
};

// This site's own public origin. CI passes the dev host on develop; the default
// targets prod so a plain `docker build` is correct.
const DOCS_URL = (
  process.env.ZUDOKU_PUBLIC_DOCS_URL ?? "https://docs.regexsolver.com"
).replace(/\/$/, "");

// Only the prod host may be indexed: the dev deploy serves the same pages, so it
// gets `noindex` and no sitemap instead of competing with prod. Local dev too.
const IS_PROD_SITE = DOCS_URL === "https://docs.regexsolver.com";

// Generated by apps/web-shared/brand/gen_og.py, copied into public/ by the
// prebuild sync-brand step. Must be absolute: scrapers reject relative images.
const OG_IMAGE = `${DOCS_URL}/og-image.png`;

// Routes that must never be indexed even on prod (they are also excluded from
// the sitemap below). The getHead plugin emits a robots meta for these.
const NOINDEX_PATHS = new Set(["/privacy", "/terms"]);

// The site-wide description: <meta name="description"> fallback and the
// og:description of routes with no frontmatter (the /api reference pages).
// Social previews truncate around 125 chars, so keep it under that.
const SITE_DESCRIPTION =
  "Documentation and API reference for RegexSolver: regex set algebra, cardinality, equivalence, and string generation.";

// Per-route frontmatter (title + description) so getHead can emit og:title /
// og:description that match each page. GENERATED by
// scripts/generate-page-meta.mjs (a predev/prebuild step): getHead only
// receives the location, and importing the MDX sources from here is not
// portable across Zudoku's two Vite pipelines. After editing frontmatter,
// restart the dev server to refresh it.
const PAGE_META: Record<
  string,
  { title?: string; description?: string } | undefined
> = pageMeta;

/* Zudoku turns a slot's `url` into an `@import url('…')` in its virtual theme
   CSS — that is how these families used to come from Google. Self-hosted now,
   so the slots name a package specifier and Vite inlines the @font-face rules
   into the entry stylesheet. One stylesheet carries all three families, hence
   the same value three times; Zudoku dedupes the imports.

   The url cannot be dropped (the schema requires it, and Zudoku re-adds its
   own Google default for a slot it treats as unconfigured) and cannot be a
   data:/no-op placeholder (postcss-import tries to open whatever it is
   given). */
const FONTS_CSS = "@regexsolver/web-shared/fonts/fonts.css";

const config: ZudokuConfig = {
  // Two-inks-on-paper identity — tokens shared with the landing page and
  // console (see apps/com.regexsolver/src/index.css).
  theme: {
    fonts: {
      sans: { url: FONTS_CSS, fontFamily: "'Instrument Sans', system-ui, sans-serif" },
      // The display face: Martian Mono, condensed via customCss below.
      serif: { url: FONTS_CSS, fontFamily: "'Martian Mono', ui-monospace, monospace" },
      mono: { url: FONTS_CSS, fontFamily: "'JetBrains Mono', ui-monospace, monospace" },
    },
    light: {
      background: "#FBFAF7",
      foreground: "#141416",
      card: "#FBFAF7",
      cardForeground: "#141416",
      popover: "#FBFAF7",
      popoverForeground: "#141416",
      primary: "#2A3FD4",
      primaryForeground: "#FBFAF7",
      secondary: "color-mix(in srgb, #141416 6%, #FBFAF7)",
      secondaryForeground: "#141416",
      muted: "color-mix(in srgb, #DAD7CE 45%, #FBFAF7)",
      mutedForeground: "#6B6960",
      accent: "color-mix(in srgb, #141416 5%, #FBFAF7)",
      accentForeground: "#141416",
      destructive: "#D13E1E",
      destructiveForeground: "#FBFAF7",
      border: "#DAD7CE",
      input: "#DAD7CE",
      ring: "#2A3FD4",
      radius: "4px",
    },
    dark: {
      background: "#161619",
      foreground: "#EDECE6",
      card: "#161619",
      cardForeground: "#EDECE6",
      popover: "color-mix(in srgb, #EDECE6 4%, #161619)",
      popoverForeground: "#EDECE6",
      primary: "#93A0F4",
      primaryForeground: "#161619",
      secondary: "color-mix(in srgb, #EDECE6 6%, #161619)",
      secondaryForeground: "#EDECE6",
      muted: "color-mix(in srgb, #2D2C32 45%, #161619)",
      mutedForeground: "#98958C",
      accent: "color-mix(in srgb, #EDECE6 5%, #161619)",
      accentForeground: "#EDECE6",
      destructive: "#EC8168",
      destructiveForeground: "#161619",
      border: "#2D2C32",
      input: "#2D2C32",
      ring: "#93A0F4",
      radius: "4px",
    },
    // Headings are set in the display face (a condensed monospace), like the
    // landing page and console.
    customCss: `
      /* Includes the category eyebrow Zudoku renders above page titles
         (CategoryHeading: a .text-primary div directly before the h1). */
      h1, h2, h3, h4,
      .text-primary:has(+ h1) {
        font-family: var(--font-serif);
        font-variation-settings: "wdth" 87.5;
        letter-spacing: -0.02em;
      }
      /* Match the website header: h-14 top bar (Header.tsx). */
      :root {
        --top-header-height: 3.5rem;
      }
      /* Restyle the built-in centred header nav (header.navigation config)
         from Zudoku's pill buttons to the website's quiet links (Header.tsx:
         text-sm text-muted-foreground, hover to foreground, 28px gaps).
         Targets Zudoku's stable data-slot component API, scoped to the header
         so the mobile drawer keeps its native styling.

         Every selector below is scoped to DIRECT children of the top-level
         list. A nav group's dropdown contains more navigation-menu-links, and
         those must keep Zudoku's padded, hover-highlighted panel styling —
         an unscoped rule flattens them to unreadable bare text. */
      header [data-slot="navigation-menu-list"] {
        gap: 1.75rem;
      }
      /* A group trigger is an inline-flex button inside the <li>, positioned on
         the li's text baseline — which lands it (and its chevron) 1px lower
         than the sibling links. Making the li a flex container centres it like
         the website nav does. Must precede the last-child display:none rule
         below (same specificity; source order decides). */
      header
        [data-slot="navigation-menu-list"]
        > [data-slot="navigation-menu-item"] {
        display: flex;
        align-items: center;
      }
      /* No open/close animation on the header dropdown: the website's panel is
         instant, and the two are kept pixel-matched. Doubled attribute beats
         the data-[state=open]:animate-in utilities on specificity. */
      header [data-slot="navigation-menu-content"][data-slot],
      header [data-slot="navigation-menu-viewport"][data-slot] {
        animation: none;
      }
      header
        [data-slot="navigation-menu-list"]
        > [data-slot="navigation-menu-item"]
        > [data-slot="navigation-menu-link"],
      header
        [data-slot="navigation-menu-list"]
        > [data-slot="navigation-menu-item"]
        > [data-slot="navigation-menu-trigger"] {
        height: auto;
        padding: 0;
        font-weight: 400;
        color: var(--muted-foreground);
        background: transparent;
      }
      header
        [data-slot="navigation-menu-list"]
        > [data-slot="navigation-menu-item"]
        > [data-slot="navigation-menu-link"]:hover,
      header
        [data-slot="navigation-menu-list"]
        > [data-slot="navigation-menu-item"]
        > [data-slot="navigation-menu-link"]:focus,
      header
        [data-slot="navigation-menu-list"]
        > [data-slot="navigation-menu-item"]
        > [data-slot="navigation-menu-trigger"]:hover,
      header
        [data-slot="navigation-menu-list"]
        > [data-slot="navigation-menu-item"]
        > [data-slot="navigation-menu-trigger"]:focus {
        color: var(--foreground);
        background: transparent;
      }
      /* The last header.navigation item (Console) is for the mobile drawer
         only — on desktop it already sits right of the bar via the
         head-navigation-end slot. */
      header
        [data-slot="navigation-menu-list"]
        > [data-slot="navigation-menu-item"]:last-child {
        display: none;
      }
    `,
  },
  metadata: {
    title: "%s | RegexSolver Docs",
    defaultTitle: "RegexSolver Documentation",
    description: SITE_DESCRIPTION,
    favicon: "/favicon.png",
    applicationName: "RegexSolver",
    publisher: "RegexSolver",
    ...(IS_PROD_SITE ? {} : { robots: "noindex, nofollow" }),
  },
  // Emits <link rel="canonical"> per route (Meta.tsx).
  canonicalUrlOrigin: DOCS_URL,
  // `exclude` is EXACT-match against route paths, no globs. Zudoku already drops
  // the 400/404/500 status pages and every `redirects` entry on its own.
  ...(IS_PROD_SITE
    ? {
        sitemap: {
          siteUrl: DOCS_URL,
          changefreq: "weekly" as const,
          priority: 0.7,
          exclude: [
            // Raw schema dump. /api and the per-tag pages stay listed.
            "/api/~schemas",
            // Real pages, linked from every footer, but not what we want ranked.
            // Also noindex'd per route via NOINDEX_PATHS in the getHead plugin.
            "/privacy",
            "/terms",
          ],
        },
      }
    : {}),
  site: {
    title: "RegexSolver Docs",
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "RegexSolver",
      // Lockup aspect is ~7.45:1 — 179px wide ≈ 24px tall, identical to the
      // website header's h-6 logo.
      width: "179px",
      href: process.env.ZUDOKU_PUBLIC_WEBSITE_URL,
    },
    showPoweredBy: false,
    footer: {},
  },
  header: {
    // Zudoku's own header nav accepts `{label, items}` groups and renders them
    // as a dropdown, so a SiteNavGroup needs no custom markup here. The website
    // reproduces the same rendering by hand in its Header.tsx (no Radix there).
    navigation: [
      ...NAV_ITEMS.map((item) =>
        isSiteNavGroup(item)
          ? {
              label: item.label,
              items: item.items.map((subItem) => ({
                label: subItem.label,
                to: resolveSiteLink(subItem, ORIGINS),
                ...(subItem.description
                  ? { description: subItem.description }
                  : {}),
                ...(isExternalSiteLink(subItem)
                  ? { target: "_blank" as const }
                  : {}),
              })),
            }
          : {
              label: item.label,
              to: resolveSiteLink(item, ORIGINS),
              ...(isExternalSiteLink(item) ? { target: "_blank" as const } : {}),
            },
      ),
      {
        label: "Console ↗",
        to: ORIGINS.console,
        target: "_blank" as const,
      },
    ],
    placements: {
      navigation: "center",
    },
  },
  plugins: [
    {
      /* Zudoku's `metadata` emits no og:/twitter: tags at all, so this adds them
         for every route. `getHead` only receives the location, so og:title /
         og:description come from PAGE_META (the frontmatter parsed above);
         routes without frontmatter (the generated /api reference) fall back to
         a generic reference title and SITE_DESCRIPTION. */
      getHead: ({ location }) => {
        const path = location.pathname.replace(/\/$/, "") || "/";
        const page = PAGE_META[path];
        const ogTitle =
          page?.title ??
          (path.startsWith("/api")
            ? "RegexSolver API Reference"
            : "RegexSolver Documentation");
        return (
          <>
            {/* The @font-face rules ride in the entry stylesheet via
                theme.fonts (FONTS_CSS above); preloads are what that cannot
                express. `crossOrigin` is mandatory even same-origin, or the
                preload is discarded and the font re-fetched. */}
            {FONT_PRELOAD.map((href) => (
              <link
                key={href}
                rel="preload"
                as="font"
                type="font/woff2"
                href={href}
                crossOrigin="anonymous"
              />
            ))}
            {NOINDEX_PATHS.has(path) && (
              <meta name="robots" content="noindex" />
            )}
            <meta property="og:title" content={ogTitle} />
            <meta
              property="og:description"
              content={page?.description ?? SITE_DESCRIPTION}
            />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="RegexSolver" />
            <meta property="og:locale" content="en_US" />
            <meta property="og:url" content={`${DOCS_URL}${location.pathname}`} />
            <meta property="og:image" content={OG_IMAGE} />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta
              property="og:image:alt"
              content="RegexSolver - set algebra for regex"
            />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image" content={OG_IMAGE} />
          </>
        );
      },
    },
  ],
  navigation: [
    {
      type: "category",
      label: "Docs",
      icon: "book",
      // Every doc item carries an explicit `label`: the sidebar otherwise
      // shows the frontmatter title, which is written long for the SERP
      // (50-60 chars with the title template) and would clutter the nav.
      items: [
        {
          type: "doc",
          file: "quickstart",
          label: "Quickstart",
          icon: "fast-forward",
          path: "/",
        },
        {
          type: "doc",
          file: "operations",
          label: "All Operations",
          icon: "table-of-contents",
        },
        {
          type: "doc",
          file: "sync-async",
          label: "Sync and Async Clients",
          icon: "arrow-left-right",
        },
        {
          type: "doc",
          file: "plans-pricing",
          label: "Plans and Pricing",
          icon: "gauge",
        },
        {
          type: "category",
          label: "Core Concepts",
          icon: "list-check",
          items: [
            {
              type: "doc",
              file: "core-concepts/terms-format",
              label: "Term's Format",
            },
            {
              type: "doc",
              file: "core-concepts/union-intersection-difference",
              label: "Union, Intersection, Difference",
            },
            {
              type: "doc",
              file: "core-concepts/concatenation-repetition",
              label: "Concatenation and Repetition",
            },
            {
              type: "doc",
              file: "core-concepts/equivalence-subset",
              label: "Equivalence and Subset",
            },
            {
              type: "doc",
              file: "core-concepts/analyzing-terms",
              label: "Analyzing a Term",
            },
            {
              type: "doc",
              file: "core-concepts/generate-strings",
              label: "Generate Strings",
            },
          ],
        },
        {
          type: "category",
          label: "Guides",
          icon: "book",
          items: [
            {
              type: "doc",
              file: "error-handling",
              label: "Error Handling",
            },
            {
              type: "doc",
              file: "best-practices",
              label: "Best Practices",
            },
          ],
        },
        {
          type: "category",
          label: "Advanced Usage",
          icon: "flask-conical",
          items: [
            {
              type: "doc",
              file: "advance-usage/determinism",
              label: "Determinism",
            },
            {
              type: "doc",
              file: "advance-usage/generation-ordering",
              label: "String Generation Ordering",
            },
            {
              type: "doc",
              file: "advance-usage/bounding-execution",
              label: "Bounding Execution",
            },
            {
              type: "doc",
              file: "advance-usage/heavy-operations",
              label: "Heavy Operations",
            },
            {
              type: "doc",
              file: "advance-usage/auto-batching",
              label: "Auto-Batching",
            },
          ],
        },
        {
          type: "link",
          icon: "folder-cog",
          label: "API Reference",
          to: "/api",
        },
      ],
    },
    {
      type: "link",
      icon: "folder-cog",
      label: "API Reference",
      to: "/api",
    },
  ],
  apis: [
    {
      type: "file",
      input: "../../shared/openapi.yaml",
      path: "/api",
      options: {
        disablePlayground: true,
        supportedLanguages: [
          { value: "java", label: "Java" },
          { value: "js", label: "JavaScript" },
          { value: "python", label: "Python" },
        ],
        generateCodeSnippet: ({ selectedLang, operation, example }) =>
          generateShortSnippet(selectedLang, operation, example),
      },
    },
  ],
  mdx: {
    components: {
      CodeTabs,
    },
  },
  slots: {
    // Identical to the website header's console link (apps/com.regexsolver
    // src/components/Header.tsx) — keep the two in sync.
    "head-navigation-end": () => (
      <a
        href={ORIGINS.console}
        aria-label="Access to your developer console"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:opacity-80 transition-opacity px-2 py-1"
      >
        Console <span aria-hidden>↗</span>
      </a>
    ),
    // The footer content component is shared with the website (Footer.tsx).
    // Zudoku's shell wrapper already applies max-w-screen-2xl px-4 lg:px-8
    // pt-20 pb-8; -mt-6 turns pt-20 into the website's pt-14. Links use the
    // component's plain-anchor default; the logo is the baked-colour SVG pair
    // because this app does not define the identity tokens the shared <Logo>
    // colours with.
    "footer-after": () => (
      <div className="w-full -mt-6">
        <FooterContent
          origins={ORIGINS}
          logo={
            <>
              <img
                src="/logo-light.svg"
                alt="RegexSolver"
                className="h-4 w-auto shrink-0 dark:hidden"
                loading="lazy"
              />
              <img
                src="/logo-dark.svg"
                alt="RegexSolver"
                className="h-4 w-auto shrink-0 hidden dark:block"
                loading="lazy"
              />
            </>
          }
        />
      </div>
    ),
  },
};

export default config;
